import React from 'react';
import { Fit, ImageSlot, ItemText, fs } from '../parts/fields';
import { EmptyNote, Head, Logo } from '../parts/chrome';
import { img, items, num, str, useSlide } from '../parts/context';
import { DeckImage } from '../types';

const grid = (n: number): { cols: number; rows: number } => {
    const cols = n <= 4 ? n : n <= 6 ? 3 : 4;
    return { cols: Math.max(1, cols), rows: Math.max(1, Math.ceil(n / Math.max(1, cols))) };
};

// #region DesignsLayout
/** A tidy grid of wallpaper designs: the lead's own (computed) or a hand-picked set. */
export const DesignsLayout: React.FunctionComponent = (): React.ReactElement => {
    const { slide, computed, edit } = useSlide();
    const manual = str(slide.fields.source) === 'manual';
    const max = Math.min(8, Math.max(2, Math.round(num(slide.fields.max, 6))));
    // Manual sets are edited in place, so edit mode reads the slide's own items; the view uses what the server resolved.
    const manualItems = items(slide.fields.items).map((it) => ({ title: str(it.title), image: img(it.image) }));
    const fromComputed = computed.designs?.items ?? null;
    const tiles: { title: string; image: DeckImage | null }[] = (manual && (edit || !fromComputed) ? manualItems : fromComputed ?? []).slice(0, max);
    const { cols, rows } = grid(tiles.length);
    const captions = tiles.some((t) => t.title) || (edit && manual);
    return (
        <>
            <div className="wd-pad">
                <Head />
                {tiles.length ? (
                    <Fit style={{ flex: 1 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, gap: rows > 1 ? 28 : 40, height: '100%', maxWidth: cols <= 2 ? 1180 : undefined }}>
                            {tiles.map((t, i) => (
                                <figure key={i} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                                    <ImageSlot fieldKey={manual ? `items.${i}.image` : `designs.${i}`} image={t.image} label="Add a design" style={{ flex: 1, minHeight: 0 }} />
                                    {captions ? (
                                        manual && edit
                                            ? <ItemText index={i} part="title" className="wd-muted" style={{ fontSize: fs(22), marginTop: 14, fontWeight: 600 }} placeholder="Design name" max={60} />
                                            : t.title ? <figcaption className="wd-muted" style={{ fontSize: fs(22), marginTop: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</figcaption> : null
                                    ) : null}
                                </figure>
                            ))}
                        </div>
                    </Fit>
                ) : (
                    <EmptyNote style={{ flex: 1 }}>
                        {computed.designs?.empty_reason || (manual ? 'Add designs to this slide.' : 'The lead’s designs show here once the deck is linked to a lead with designs.')}
                    </EmptyNote>
                )}
            </div>
            <Logo />
        </>
    );
};
